import React, { useState } from 'react';
import { X, Check, Zap, Loader2 } from 'lucide-react';
import { createCheckoutSession } from '../services/paymentService';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// NOTE: Ensure these are PRICE IDs (starting with 'price_') from your Stripe Dashboard.
const STRIPE_PRICES = {
    STARTER: 'price_1SW5GsPwQdPU78i8SZOz58C3', 
    PRO: 'price_1SW5HqPwQdPU78i8bvbb9N9G'
};

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose }) => {
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);

  const handleBuy = async (priceId: string) => {
    setLoadingPriceId(priceId);
    try {
        await createCheckoutSession(priceId);
        // The window will redirect, so loading state persists
    } catch (error) {
        console.error(error);
        alert("Unable to start checkout. Please try again.");
        setLoadingPriceId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-white border-2 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 md:p-8 rotate-1 animate-in zoom-in-95 duration-300">
        
        <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
            <X size={24} />
        </button>

        <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-300 border-2 border-black rounded-full mb-4 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                <Zap size={24} className="fill-black" />
            </div>
            <h2 className="text-3xl font-bold font-hand">Refill Your Ink</h2>
            <p className="text-gray-600 font-hand mt-2">Keep the sketches flowing. Simple pricing, no subscriptions.</p>
        </div>

        <div className="space-y-4">
            {/* Starter Pack */}
            <div 
                className="border-2 border-black p-4 hover:bg-yellow-50 cursor-pointer transition-colors relative overflow-hidden group"
                onClick={() => handleBuy(STRIPE_PRICES.STARTER)}
            >
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="font-bold font-hand text-xl">Starter Pack</h3>
                        <p className="text-gray-500 font-hand text-sm">50 Credits</p>
                    </div>
                    <div className="text-2xl font-bold font-hand">$5.00</div>
                </div>
                <button 
                    disabled={loadingPriceId !== null}
                    className="w-full mt-3 bg-black text-white py-2 rounded font-bold font-hand hover:bg-gray-800 transition-colors flex justify-center"
                >
                    {loadingPriceId === STRIPE_PRICES.STARTER ? <Loader2 className="animate-spin" /> : 'Buy Now'}
                </button>
            </div>

            {/* Pro Pack */}
            <div 
                className="border-2 border-black p-4 bg-yellow-300 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] relative overflow-hidden group cursor-pointer hover:brightness-105 transition-all"
                onClick={() => handleBuy(STRIPE_PRICES.PRO)}
            >
                <div className="absolute top-0 right-0 bg-black text-white text-[10px] px-2 py-0.5 font-bold uppercase">Best Value</div>
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="font-bold font-hand text-xl">Pro Pack</h3>
                        <p className="text-gray-800 font-hand text-sm">200 Credits</p>
                    </div>
                    <div className="text-2xl font-bold font-hand">$15.00</div>
                </div>
                <button 
                    disabled={loadingPriceId !== null}
                    className="w-full mt-3 bg-white text-black border-2 border-black py-2 rounded font-bold font-hand hover:bg-gray-50 transition-colors flex justify-center"
                >
                    {loadingPriceId === STRIPE_PRICES.PRO ? <Loader2 className="animate-spin" /> : 'Buy Now'}
                </button>
            </div>
        </div>

        <div className="mt-6 text-center">
            <p className="text-xs text-gray-400 font-hand">
                Payments are secure and processed by Stripe.
            </p>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;